(() => {
  const CONFIG_SELECTOR = '[data-store-availability-config]';
  const INVENTORY_DATA_SELECTOR = '[data-inventory-data]';
  const INVENTORY_ROOT_SELECTOR = '[data-inventory-react-target]';
  const STORE_LOCATIONS_SELECTOR = '[data-store-locations-data]';
  const STORE_ROW_SELECTOR = '.store-drawer-location';
  const STORE_ROW_NAME_SELECTOR = '.store-drawer-location__name';
  const STORE_ROW_STATUS_SELECTOR = '.store-drawer-location__pickup-status';
  const STORE_ROW_STATUS_ICON_CLASS = 'store-drawer-location__pickup-satus-icon';
  const FILTER_INPUT_SELECTOR = '.locations-drawer__show-only-stores-with-inventory input';
  const STATUS_CLASS_IN_STOCK = 'in-stock';
  const STATUS_CLASS_LOW_STOCK = 'low-stock';
  const STATUS_CLASS_SOLD_OUT = 'sold-out';
  const STATUS_CLASS_LOADING = 'loading';

  const QUERY = `
    query VariantStoreAvailability($id: ID!, $country: CountryCode, $language: LanguageCode)
    @inContext(country: $country, language: $language) {
      node(id: $id) {
        ... on ProductVariant {
          id
          storeAvailability(first: 250) {
            nodes {
              available
              quantityAvailable
              pickUpTime
              location {
                id
                name
              }
            }
          }
        }
      }
    }
  `;

  const FALLBACK_STRINGS = {
    pickupAvailable: 'Pick-up available today',
    pickupUnavailable: 'Unavailable for pickup',
    availableInStore: 'Available in store',
    unavailableInStore: 'Unavailable in store',
    inStorePickup: 'Pickup in Store',
    inStoreAvailability: 'In-Store Purchase',
    pickupAvailableInAt: 'Pickup [time] at ',
    pickupUnavailableAt: 'Pickup unavailable at ',
    inStockAt: 'In stock at ',
    soldOutAt: 'Sold out at ',
  };

  let config = null;
  let inventoryData = null;
  let storeLocations = [];
  let storeByLocationId = new Map();
  let storeByName = new Map();
  let availabilityCache = new Map();
  let currentAvailability = null;
  let currentAvailabilityVariantGid = null;
  let currentVariantGid = null;
  let latestRequestId = 0;
  let observer = null;
  let observedRoot = null;
  let applyTimers = new Set();
  let fetchPatched = false;
  let selectedStatusTemplates = null;
  let floatingButtonTemplate = null;

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  }

  function parseJsonScript(selector) {
    const script = document.querySelector(selector);

    if (!script) {
      return null;
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(`Store availability: failed to parse ${selector}`, error);
      return null;
    }
  }

  function normalizeLocationId(id) {
    return String(id || '').split('/').pop();
  }

  function normalizeStoreName(name) {
    return String(name || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function variantIdToGid(variantId) {
    const normalizedVariantId = String(variantId || '').trim();

    if (!normalizedVariantId) {
      return '';
    }

    if (normalizedVariantId.startsWith('gid://')) {
      return normalizedVariantId;
    }

    return `gid://shopify/ProductVariant/${normalizedVariantId}`;
  }

  function getVariantGidFromEventDetail(detail) {
    if (!detail) {
      return '';
    }

    if (typeof detail === 'string' || typeof detail === 'number') {
      return getVariantGid(String(detail));
    }

    if (detail.gid) {
      return detail.gid;
    }

    if (detail.variant?.gid) {
      return detail.variant.gid;
    }

    if (detail.id) {
      return getVariantGid(String(detail.id));
    }

    if (detail.variant?.id) {
      return getVariantGid(String(detail.variant.id));
    }

    return '';
  }

  function getVariantGid(variantId) {
    const id = String(variantId || '').replace('gid://shopify/ProductVariant/', '');
    const variant = inventoryData?.variants?.[id];

    return variant?.gid || variantIdToGid(id);
  }

  function getCurrentVariantGidFromPage() {
    const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"]');

    if (variantInput?.value) {
      return getVariantGid(variantInput.value);
    }

    const variantIdFromUrl = new URL(window.location.href).searchParams.get('variant');

    if (variantIdFromUrl) {
      return getVariantGid(variantIdFromUrl);
    }

    return config?.selectedVariantGid || getVariantGid(config?.selectedVariantId);
  }

  function buildStoreIndexes() {
    storeByLocationId = new Map();
    storeByName = new Map();

    for (const store of storeLocations) {
      const locationId = normalizeLocationId(store.shopify_id);
      const storeName = normalizeStoreName(store.name);

      if (locationId) {
        storeByLocationId.set(locationId, store);
      }

      if (storeName) {
        storeByName.set(storeName, store);
      }
    }
  }

  function getSelectedStore() {
    const selectedStoreId = localStorage.getItem('theme:my_store');

    if (selectedStoreId) {
      const selectedStore = storeLocations.find((store) => String(store.id) === String(selectedStoreId));

      if (selectedStore) {
        return selectedStore;
      }
    }

    return storeLocations[0] || null;
  }

  function mapStoreAvailability(node) {
    const quantityAvailable = Number(node.quantityAvailable || 0);
    const locationId = normalizeLocationId(node.location?.id);

    return {
      locationId,
      storefrontLocationId: node.location?.id,
      locationName: node.location?.name,
      quantityAvailable,
      hasInventory: quantityAvailable > 0,
      pickupAvailable: quantityAvailable > 0 && Boolean(node.available),
      pickupTime: node.pickUpTime,
    };
  }

  async function fetchAvailability(variantGid) {
    if (availabilityCache.has(variantGid)) {
      return availabilityCache.get(variantGid);
    }

    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.accessToken,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: {
          id: variantGid,
          country: config.country || null,
          language: config.language || null,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront API request failed with HTTP ${response.status}`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      const message = payload.errors.map((error) => error.message).join('; ');
      throw new Error(message);
    }

    const nodes = payload.data?.node?.storeAvailability?.nodes || [];
    const availabilityByLocation = new Map();

    for (const node of nodes) {
      const availability = mapStoreAvailability(node);

      if (availability.locationId) {
        availabilityByLocation.set(availability.locationId, availability);
      }
    }

    availabilityCache.set(variantGid, availabilityByLocation);
    return availabilityByLocation;
  }

  async function loadAvailability(variantGid) {
    if (!variantGid) {
      return;
    }

    const requestId = latestRequestId + 1;
    latestRequestId = requestId;
    currentVariantGid = variantGid;
    currentAvailability = null;
    currentAvailabilityVariantGid = null;
    markSelectedStoreLoading();

    try {
      const availabilityByLocation = await fetchAvailability(variantGid);

      if (requestId !== latestRequestId) {
        return;
      }

      currentAvailability = availabilityByLocation;
      currentAvailabilityVariantGid = variantGid;
      applyAvailability();
      scheduleApplySeries();
    } catch (error) {
      if (requestId !== latestRequestId) {
        return;
      }

      console.warn('Store availability: unable to fetch Storefront API availability', error);
      currentAvailability = new Map();
      currentAvailabilityVariantGid = variantGid;
      applyAvailability();
    }
  }

  function setStatusClass(element, statusClass) {
    if (!element) {
      return;
    }

    const classesToRemove = [
      STATUS_CLASS_IN_STOCK,
      STATUS_CLASS_LOW_STOCK,
      STATUS_CLASS_SOLD_OUT,
      STATUS_CLASS_LOADING,
    ].filter((className) => className !== statusClass && element.classList.contains(className));

    for (const className of classesToRemove) {
      element.classList.remove(className);
    }

    if (!element.classList.contains(statusClass)) {
      element.classList.add(statusClass);
    }

    if (element.getAttribute('aria-busy') !== 'false') {
      element.setAttribute('aria-busy', 'false');
    }
  }

  function markSelectedStoreLoading() {
    const root = document.querySelector(INVENTORY_ROOT_SELECTOR);

    if (!root) {
      return;
    }

    for (const statusElement of getSelectedStoreStatusElements(root)) {
      statusElement.classList.add(STATUS_CLASS_LOADING);
      statusElement.setAttribute('aria-busy', 'true');
    }
  }

  function getSelectedStoreStatusElements(root) {
    const statusElements = Array.from(root.querySelectorAll('.inventory-status__status'));

    return statusElements.filter((statusElement) => !statusElement.closest(STORE_ROW_SELECTOR));
  }

  function captureSelectedStoreTemplates(root, statusElements) {
    if (statusElements.length >= 2 && !statusElements[0].dataset.storeAvailabilityFallback) {
      selectedStatusTemplates = statusElements.slice(0, 2).map((statusElement) => statusElement.cloneNode(true));
    }

    const floatingButton = root.querySelector('.inventory-status__floating-button');

    if (floatingButton && !floatingButton.dataset.storeAvailabilityFallback) {
      floatingButtonTemplate = floatingButton.cloneNode(true);
    }
  }

  function openInventoryDrawer() {
    window.dispatchEvent(new CustomEvent('open_locations_drawer_inventory'));
    scheduleApplySeries();
  }

  function wireFallbackTrigger(trigger) {
    if (!trigger) {
      return;
    }

    trigger.type = 'button';
    trigger.dataset.storeAvailabilityFallbackTrigger = 'true';

    if (trigger.dataset.storeAvailabilityFallbackTriggerBound === 'true') {
      return;
    }

    trigger.dataset.storeAvailabilityFallbackTriggerBound = 'true';
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openInventoryDrawer();
    });
  }

  function prepareFallbackElement(element) {
    element.dataset.storeAvailabilityFallback = 'true';
    element.querySelectorAll('.inventory-status__text-link').forEach((button) => {
      wireFallbackTrigger(button);
    });

    return element;
  }

  function updateFallbackStoreNames(element) {
    const selectedStore = getSelectedStore();

    if (!selectedStore) {
      return;
    }

    element.querySelectorAll('.inventory-status__text-link').forEach((button) => {
      button.textContent = selectedStore.name;
    });
  }

  function cloneSelectedStatusTemplate(index) {
    const template = selectedStatusTemplates?.[index];

    if (!template) {
      return null;
    }

    const clone = prepareFallbackElement(template.cloneNode(true));
    updateFallbackStoreNames(clone);

    return clone;
  }

  function ensureFloatingButton(root) {
    if (root.querySelector('.inventory-status__floating-button')) {
      return;
    }

    if (!floatingButtonTemplate) {
      return;
    }

    const clone = floatingButtonTemplate.cloneNode(true);
    clone.dataset.storeAvailabilityFallback = 'true';
    wireFallbackTrigger(clone);
    root.append(clone);
  }

  function createInventoryStatusText(className, text) {
    const textElement = document.createElement('span');
    textElement.className = `inventory-status__text ${className}`;
    textElement.textContent = text;

    return textElement;
  }

  function createSelectedStoreStatusElement(title, inStockText, soldOutText) {
    const statusElement = document.createElement('div');
    statusElement.className = 'inventory-status__status loading';
    statusElement.dataset.storeAvailabilityFallback = 'true';

    const titleElement = document.createElement('div');
    titleElement.className = 'inventory-status__status-title';
    titleElement.textContent = title;

    const contentElement = document.createElement('span');
    contentElement.className = 'inventory-status__content';
    contentElement.append(
      createInventoryStatusText('inventory-status__text--in-stock', inStockText),
      createInventoryStatusText('inventory-status__text--sold-out', soldOutText)
    );

    statusElement.append(titleElement, contentElement);

    return statusElement;
  }

  function ensureSelectedStoreStatusElements(root) {
    const statusElements = getSelectedStoreStatusElements(root);

    if (statusElements.length >= 2) {
      captureSelectedStoreTemplates(root, statusElements);
      ensureFloatingButton(root);
      return statusElements;
    }

    const selectedStore = getSelectedStore();
    const storeName = selectedStore?.name || '';
    const strings = { ...FALLBACK_STRINGS, ...(config.strings || {}) };
    const pickupTime = currentAvailability?.get(normalizeLocationId(selectedStore?.shopify_id))?.pickupTime || '';
    const pickupAvailableText = pickupTime
      ? `${strings.pickupAvailableInAt.replace('[time]', pickupTime)}${storeName}`.trim()
      : `${strings.pickupAvailable} ${storeName}`.trim();
    const pickupUnavailableText = `${strings.pickupUnavailableAt}${storeName}`.trim();
    const inStoreAvailableText = `${strings.inStockAt}${storeName}`.trim();
    const inStoreUnavailableText = `${strings.soldOutAt}${storeName}`.trim();
    const pickupStatusElement = cloneSelectedStatusTemplate(0) || createSelectedStoreStatusElement(
      strings.inStorePickup,
      pickupAvailableText,
      pickupUnavailableText
    );
    const inStoreStatusElement = cloneSelectedStatusTemplate(1) || createSelectedStoreStatusElement(
      strings.inStoreAvailability,
      inStoreAvailableText,
      inStoreUnavailableText
    );

    prepareFallbackElement(pickupStatusElement);
    prepareFallbackElement(inStoreStatusElement);
    updateFallbackStoreNames(pickupStatusElement);
    updateFallbackStoreNames(inStoreStatusElement);
    ensureFloatingButton(root);

    const floatingButton = root.querySelector('.inventory-status__floating-button');

    if (floatingButton) {
      root.insertBefore(inStoreStatusElement, floatingButton);
      root.insertBefore(pickupStatusElement, inStoreStatusElement);
    } else {
      root.prepend(pickupStatusElement, inStoreStatusElement);
    }

    return getSelectedStoreStatusElements(root);
  }

  function updateSelectedStoreStatus(root) {
    const selectedStore = getSelectedStore();

    if (!selectedStore) {
      return;
    }

    const selectedAvailability = currentAvailability?.get(normalizeLocationId(selectedStore.shopify_id));
    const statusElements = ensureSelectedStoreStatusElements(root);
    const pickupStatusElement = statusElements[0];
    const inStoreStatusElement = statusElements[1];
    const pickupStatus = selectedAvailability?.pickupAvailable ? STATUS_CLASS_IN_STOCK : STATUS_CLASS_SOLD_OUT;
    const inStoreStatus = selectedAvailability?.hasInventory ? STATUS_CLASS_IN_STOCK : STATUS_CLASS_SOLD_OUT;

    setStatusClass(pickupStatusElement, pickupStatus);
    setStatusClass(inStoreStatusElement, inStoreStatus);

    const quantity = String(selectedAvailability?.quantityAvailable || 0);

    if (pickupStatusElement && pickupStatusElement.dataset.storeAvailabilityQuantity !== quantity) {
      pickupStatusElement.dataset.storeAvailabilityQuantity = quantity;
    }

    if (inStoreStatusElement && inStoreStatusElement.dataset.storeAvailabilityQuantity !== quantity) {
      inStoreStatusElement.dataset.storeAvailabilityQuantity = quantity;
    }
  }

  function getStoreFromRow(row) {
    const nameElement = row.querySelector(STORE_ROW_NAME_SELECTOR);
    const storeName = normalizeStoreName(nameElement?.textContent);

    return storeByName.get(storeName) || null;
  }

  function updateInlineStatusText(statusElement, text) {
    if (!statusElement || statusElement.textContent.trim() === text) {
      return;
    }

    let icon = statusElement.querySelector(`.${STORE_ROW_STATUS_ICON_CLASS}`);

    if (!icon) {
      icon = document.createElement('div');
      icon.className = STORE_ROW_STATUS_ICON_CLASS;
    }

    statusElement.textContent = '';
    statusElement.append(icon, document.createTextNode(` ${text}`));
  }

  function updateDrawerRow(row, availability) {
    const statusElements = row.querySelectorAll(STORE_ROW_STATUS_SELECTOR);
    const pickupStatusElement = statusElements[0];
    const inStoreStatusElement = statusElements[1];
    const strings = { ...FALLBACK_STRINGS, ...(config.strings || {}) };
    const pickupStatus = availability?.pickupAvailable ? STATUS_CLASS_IN_STOCK : STATUS_CLASS_SOLD_OUT;
    const inStoreStatus = availability?.hasInventory ? STATUS_CLASS_IN_STOCK : STATUS_CLASS_SOLD_OUT;

    setStatusClass(pickupStatusElement, pickupStatus);
    setStatusClass(inStoreStatusElement, inStoreStatus);

    updateInlineStatusText(
      pickupStatusElement,
      availability?.pickupAvailable ? strings.pickupAvailable : strings.pickupUnavailable
    );
    updateInlineStatusText(
      inStoreStatusElement,
      availability?.hasInventory ? strings.availableInStore : strings.unavailableInStore
    );

    const quantity = String(availability?.quantityAvailable || 0);

    if (row.dataset.storeAvailabilityQuantity !== quantity) {
      row.dataset.storeAvailabilityQuantity = quantity;
    }

    if (row.dataset.storeAvailabilityCorrected !== 'true') {
      row.dataset.storeAvailabilityCorrected = 'true';
    }
  }

  function updateDrawerRows(root) {
    const filterInput = root.querySelector(FILTER_INPUT_SELECTOR);
    const shouldShowOnlyAvailableStores = Boolean(filterInput?.checked);

    for (const row of root.querySelectorAll(STORE_ROW_SELECTOR)) {
      const store = getStoreFromRow(row);
      const availability = store ? currentAvailability?.get(normalizeLocationId(store.shopify_id)) : null;
      const hasInventory = Boolean(availability?.hasInventory);

      updateDrawerRow(row, availability);

      if (shouldShowOnlyAvailableStores) {
        row.hidden = !hasInventory;
      } else if (row.hidden) {
        row.hidden = false;
      }
    }
  }

  function applyAvailability() {
    const pageVariantGid = getCurrentVariantGidFromPage();

    if (pageVariantGid && pageVariantGid !== currentVariantGid) {
      loadAvailability(pageVariantGid);
      return;
    }

    if (!currentAvailability || currentAvailabilityVariantGid !== currentVariantGid) {
      return;
    }

    observeInventoryRoot();

    const root = document.querySelector(INVENTORY_ROOT_SELECTOR);

    if (!root) {
      return;
    }

    updateSelectedStoreStatus(root);
    updateDrawerRows(root);
  }

  function scheduleApply(delay = 50) {
    const timer = window.setTimeout(() => {
      applyTimers.delete(timer);
      applyAvailability();
    }, delay);

    applyTimers.add(timer);
  }

  function scheduleApplySeries() {
    for (const delay of [0, 75, 150, 300, 600, 1000, 1800, 3000]) {
      scheduleApply(delay);
    }
  }

  function observeInventoryRoot() {
    const root = document.querySelector(INVENTORY_ROOT_SELECTOR);

    if (!root) {
      return;
    }

    if (observer && observedRoot === root) {
      return;
    }

    observer?.disconnect();
    observedRoot = root;

    observer = new MutationObserver(() => {
      scheduleApply(40);
    });

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'aria-busy', 'data-store-availability-corrected'],
      characterData: true,
      childList: true,
      subtree: true,
    });
  }

  function bindEvents() {
    window.addEventListener('variantChanged', (event) => {
      const variantGid = getVariantGidFromEventDetail(event.detail) || getCurrentVariantGidFromPage();

      if (!variantGid) {
        return;
      }

      loadAvailability(variantGid);
      scheduleApplySeries();
    });

    window.addEventListener('myStoreChanged', () => {
      scheduleApplySeries();
    });

    window.addEventListener('open_locations_drawer_inventory', () => {
      scheduleApplySeries();
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const inventoryTrigger = event.target.closest('.inventory-status__text-link, .inventory-status__floating-button');

      if (!inventoryTrigger || !inventoryTrigger.closest(INVENTORY_ROOT_SELECTOR)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openInventoryDrawer();
    }, true);

    document.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (event.target.matches(FILTER_INPUT_SELECTOR)) {
        scheduleApplySeries();
        return;
      }

      if (event.target.matches('[name="id"]')) {
        const variantGid = getVariantGid(event.target.value);

        if (variantGid) {
          loadAvailability(variantGid);
          scheduleApplySeries();
        }
      }
    });
  }

  function bindFetchFallback() {
    if (fetchPatched || typeof window.fetch !== 'function') {
      return;
    }

    const originalFetch = window.fetch.bind(window);
    fetchPatched = true;

    window.fetch = async (...args) => {
      const request = args[0];
      const url = typeof request === 'string' ? request : request?.url;
      const response = await originalFetch(...args);

      if (url && String(url).includes('view=location_inventories')) {
        try {
          const requestUrl = new URL(String(url), window.location.origin);
          const variantId = requestUrl.searchParams.get('variant');
          const variantGid = getVariantGid(variantId);

          if (variantGid) {
            loadAvailability(variantGid);
            scheduleApplySeries();
          }
        } catch (error) {
          console.warn('Store availability: unable to inspect variant inventory request', error);
        }
      }

      return response;
    };
  }

  function init() {
    config = parseJsonScript(CONFIG_SELECTOR);
    inventoryData = parseJsonScript(INVENTORY_DATA_SELECTOR);
    storeLocations = parseJsonScript(STORE_LOCATIONS_SELECTOR)?.locations || [];

    if (!document.querySelector(INVENTORY_ROOT_SELECTOR) || !config || !inventoryData || !storeLocations.length) {
      return;
    }

    if (!config.endpoint || !config.accessToken) {
      console.warn('Store availability: missing Storefront API endpoint or access token');
      return;
    }

    buildStoreIndexes();
    bindEvents();
    bindFetchFallback();
    observeInventoryRoot();

    currentVariantGid = getCurrentVariantGidFromPage();
    loadAvailability(currentVariantGid);
  }

  onReady(init);
})();
